import type { EDUCATIONAL_ADVISOR } from "@academy-context/shared/domain/types/enums/education-advisors";
import type { TRAINING_ITEM_TYPE, TRAINING_ITEM_STATUS, TrainingItem } from "@academy-context/shared/domain/types/training-items";
import type { OBJECTIVE_ACQUISITION_STATUS } from "@academy-context/read/domain/types/training-follow-up";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { SURVEY_QUESTION_TYPE } from "@academy-context/shared/domain/types/enums/survey";
import type { DateInterval } from "@shared-kernel/core/types/date-interval";
import type { ApiTrainingSchedule } from "@shared-kernel/core/types/api-training-schedule";
import type { EXTERNAL_PROVIDER_BATCH_STUDENT_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import type { ATTACHMENT_LABEL } from "@academy-context/shared/external-provider-batch/types/provider-training-form.types";
import type { FUNDER, FUNDER_TYPE } from "@academy-context/shared/domain/types/enums/funders";
import type { BATCH_ORGANIZER_TYPE } from "@academy-context/shared/domain/types/enums/batch-organizer-type";
import type { MAIN_SPECIALIZATIONS } from "@academy-context/shared/domain/types/enums/specializations";
import type { FUNDING_REQUEST_STATUS } from "@academy-context/read/domain/types/enums/funding-request-status";
import type { EligibleTrainingItemsVM } from "@academy-context/shared/domain/types/eligible-training-items";
import type { VoiceNoteVM } from "@academy-context/shared/domain/types/voice-note";

// ========================================
// Attendance Sheets Types
// ========================================

// New attendance sheet type structure for Student API
interface BaseAttendanceSheetVM {
  id: string;
  studentSignature: Nullable<{
    url: string;
    signedAt: string;
  }>;
}

export interface LessonAttendanceSheetVM extends BaseAttendanceSheetVM {
  type: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  date: string;
  hours: number;
  details: {
    educationalAdvisor: {
      type: EDUCATIONAL_ADVISOR;
      userName: string;
    };
    lesson: {
      studio: string;
      studioPrice: number;
      hourlyPrice: number;
      totalPrice: number;
      // `null` si le prof n'a pas laissé de note vocale — elle est facultative.
      voiceNote: Nullable<VoiceNoteVM>;
    };
  };
}

export interface ProviderBatchAttendanceSheetVM extends BaseAttendanceSheetVM {
  type: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  interval: DateInterval;
  details: {
    label: string;
    educationalAdvisor: {
      type: EDUCATIONAL_ADVISOR;
      userName: string;
    };
  };
}

export interface ExternalProviderBatchAttendanceSheetVM extends BaseAttendanceSheetVM {
  type: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  interval: DateInterval;
  details: {
    label: string;
    educationalAdvisor: {
      type: EDUCATIONAL_ADVISOR;
      userName: string;
    };
  };
}

// TODO: merge with ProviderBatchAttendanceSheetVM i think
export interface TeacherBatchAttendanceSheetVM extends BaseAttendanceSheetVM {
  type: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  interval: DateInterval;
  details: {
    label: string;
    educationalAdvisor: {
      type: EDUCATIONAL_ADVISOR;
      userName: string;
    };
  };
}

export type AttendanceSheetVM =
  | LessonAttendanceSheetVM
  | ProviderBatchAttendanceSheetVM
  | TeacherBatchAttendanceSheetVM
  | ExternalProviderBatchAttendanceSheetVM;

// Signature constraints
export const SIGNATURE_CONSTRAINTS = {
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  minWidth: 140, // pixels
  minHeight: 100, // pixels
  allowedFormats: ["jpg"] as const, // Format JPEG uniquement
};

export interface SignAttendanceSheetBody {
  attendanceSheetId: string;
  type: TRAINING_ITEM_TYPE;
  signature: File; // Image JPG, 140x100px min, max 10MB
}

export interface SignAttendanceSheetResponse {
  url: string; // URL publique de la signature uploadée
}

export interface SignAllAttendanceSheetsBody {
  signature: File; // Image JPG, 140x100px min, max 10MB
}

export interface SignAllAttendanceSheetsResponse {
  signedLessonSheetIds: string[];
  signedBatchSheetIds: string[];
  totalSigned: number;
}

// ========================================
// Manage Objectives Types
// ========================================

// L'élément de formation se désigne par le couple (type, id) : `trainingItemId` porte le suivi de
// formation pour un coaching, la participation pour une masterclass.
//
// Deux variantes seulement, et leur différence est celle du domaine : qui assigne le conseiller.
// Sur un coaching, l'élève le choisit ; sur une masterclass, l'objectif est pré-assigné à
// l'organisateur dès sa création — l'élève peut seulement abandonner ou ajouter un objectif.

export interface LessonManageObjectivesCommand {
  type: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  trainingItemId: string;
  existingObjectives: {
    objectiveId: string;
    isAbandoned: boolean;
    educationalAdvisor: {
      id: string;
      type: EDUCATIONAL_ADVISOR;
    } | null;
  }[];
  newObjectives: {
    quantifiableObjective: string;
    educationalAdvisor: {
      id: string;
      type: EDUCATIONAL_ADVISOR;
    };
  }[];
}

export interface BatchManageObjectivesCommand {
  type: TRAINING_ITEM_TYPE.PROVIDER_BATCH | TRAINING_ITEM_TYPE.TEACHER_BATCH | TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  trainingItemId: string;
  existingObjectives: {
    objectiveId: string;
    isAbandoned: boolean;
  }[];
  newObjectives: {
    // Le conseiller est l'organisateur de la masterclass : rien à désigner ici.
    quantifiableObjective: string;
  }[];
}

export type ManageObjectivesCommand = LessonManageObjectivesCommand | BatchManageObjectivesCommand;

// ========================================
// Submit Survey Types
// ========================================

interface BaseSubmitSurveyCommand {
  type: TRAINING_ITEM_TYPE;
  surveyAnswers: {
    questionId: string;
    value: string | number; // string for TEXT, number for RATING
  }[];
}

export interface LessonSubmitSurveyCommand extends BaseSubmitSurveyCommand {
  type: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  trainingFollowUpId: string;
}

export interface BatchSubmitSurveyCommand extends BaseSubmitSurveyCommand {
  type: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  participationId: string;
}

export interface TeacherBatchSubmitSurveyCommand extends BaseSubmitSurveyCommand {
  type: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  participationId: string;
}

export interface ExternalProviderBatchSubmitSurveyCommand extends BaseSubmitSurveyCommand {
  type: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  participationId: string;
}

export type SubmitSurveyCommand =
  | LessonSubmitSurveyCommand
  | BatchSubmitSurveyCommand
  | TeacherBatchSubmitSurveyCommand
  | ExternalProviderBatchSubmitSurveyCommand;

// ========================================
// Student Pending Actions Types
// ========================================

export interface StudentPendingActionsVM {
  hasActions: boolean;
  attendanceSheets: {
    pendingCount: number;
  };
  surveys: {
    pendingCount: number;
  };
  objectives: {
    pendingCount: number;
  };
}

// ========================================
// Pending Surveys Types
// ========================================

export interface PendingSurveyQuestionVM {
  id: string;
  text: string;
  type: SURVEY_QUESTION_TYPE;
  options: { min: number; max: number } | null;
}

interface BasePendingSurveyVM {
  id: string;
  trainingContext: {
    title: string;
    interval: DateInterval;
  };
  questions: PendingSurveyQuestionVM[];
}

export interface PendingBatchSurveyVM extends BasePendingSurveyVM {
  surveyType: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  participationId: string;
  batchId: string;
}

export interface PendingLessonSurveyVM extends BasePendingSurveyVM {
  surveyType: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  trainingFollowUpId: string;
}

export interface PendingExternalProviderBatchSurveyVM extends BasePendingSurveyVM {
  surveyType: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  participationId: string;
}

export interface PendingTeacherBatchSurveyVM extends BasePendingSurveyVM {
  surveyType: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  participationId: string;
  batchId: string;
}

export type PendingSurveyVM =
  | PendingBatchSurveyVM
  | PendingTeacherBatchSurveyVM
  | PendingLessonSurveyVM
  | PendingExternalProviderBatchSurveyVM;

// ========================================
// Pending Objective Assignments Types (ONE_ON_ONE_LESSONS only)
// ========================================

export interface PendingObjectiveVM {
  id: string;
  quantifiableObjective: string;
  number: number;
  isCustom: boolean;
}

export interface PendingObjectiveAssignmentGroupVM {
  trainingFollowUpId: string;
  trainingContext: {
    title: string;
    interval: DateInterval;
  };
  objectives: PendingObjectiveVM[];
}

// ========================================
// Training Items Types
// ========================================

interface BaseTrainingItemVM {
  id: string;
  title: string;
  interval: {
    start: string;
    end: string;
  };
  isActionRequired: boolean;
}

export interface LessonTrainingItemVM extends BaseTrainingItemVM {
  itemType: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  status: TRAINING_ITEM_STATUS;
  details: {
    label: string; // Teacher names (e.g., "Jean Dupont, Marie Martin")
  };
}

export interface ProviderBatchTrainingItemVM extends BaseTrainingItemVM {
  itemType: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  status: TRAINING_ITEM_STATUS;
  details: {
    price: number;
    label: string; // Provider name
  };
}

export interface ExternalProviderBatchTrainingItemVM extends BaseTrainingItemVM {
  requestId: string;
  participationId: Nullable<string>;
  itemType: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  status: EXTERNAL_PROVIDER_BATCH_STUDENT_MERGED_STATUS;
  details: {
    price: number;
    label: string;
  };
}

export interface TeacherBatchTrainingItemVM extends BaseTrainingItemVM {
  itemType: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  status: TRAINING_ITEM_STATUS;
  details: {
    price: number;
    label: string; // Teacher name
  };
}

export type TrainingItemVM =
  | LessonTrainingItemVM
  | ProviderBatchTrainingItemVM
  | TeacherBatchTrainingItemVM
  | ExternalProviderBatchTrainingItemVM;

// ========================================
// Training Item Details Types
// ========================================

export interface ObjectiveVM {
  id: string;
  quantifiableObjective: string;
  number: number;
  assignedEducationalAdvisor: Nullable<{
    id: string;
    type: EDUCATIONAL_ADVISOR;
    name: string;
  }>;
  assignmentDate: Nullable<string>;
  acquisitionStatus: Nullable<OBJECTIVE_ACQUISITION_STATUS>;
  isAbandoned: boolean;
  isCustom: boolean;
}

// La forme servie à l'élève ne porte PAS l'appréciation privée : elle n'a pas d'emplacement où
// atterrir, plutôt qu'un champ filtré — c'est un filtrage par champ qui avait laissé fuir la
// précédente.
export interface StudentTrainingItemAppraisalVM {
  id: string;
  educationalAdvisor: { id: string; type: EDUCATIONAL_ADVISOR; name: Nullable<string> };
  disclosedAppraisal: string;
  submittedAt: string;
}

export interface TrainingItemDetailsVM {
  id: string;
  training: {
    title: string;
    interval: DateInterval;
    educationalAdvisors: {
      fullName: string;
    }[];
  };
  itemType: TRAINING_ITEM_TYPE;
  objectives: ObjectiveVM[];
  // Une appréciation par conseiller ayant rendu son évaluation sur cet élément de formation.
  appraisals: StudentTrainingItemAppraisalVM[];
  survey: Nullable<SurveyVM>;
  entryAttestationUrl: Nullable<string>;
  completionAttestationUrl: Nullable<string>;
}

export interface SurveyQuestionVM {
  id: string;
  text: string;
  type: SURVEY_QUESTION_TYPE;
  options: {
    min: number;
    max: number;
  } | null;
  response: string | number | null;
}

export interface SurveyVM {
  id: string;
  questions: SurveyQuestionVM[];
  submittedAt: Nullable<string>;
}

// ========================================
// Student Document Upload Constants
// ========================================

export const STUDENT_DOCUMENT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
export const STUDENT_DOCUMENT_FILETYPE = ["application/pdf"];

// ========================================
// External Provider Batch Request Types
// ========================================

export interface CreateExternalProviderBatchRequestBody {
  providerName: string;
  providerContactFirstName: string;
  providerContactLastName: string;
  providerEmail: string;
  providerPhone: string;
  title: string;
  description: string;
  objective: string;
  price: number;
  trainingSchedules: ApiTrainingSchedule[];
  attachments: { url: string; label: ATTACHMENT_LABEL }[];
  websiteUrl: string | null;
  studentNote: string | null;
}

// ========================================
// Student Summary Types (merged from StudentGateway)
// ========================================

export interface StudentSummaryVM {
  balance: number;
  afdasISWaitingPeriodEndDate: Nullable<string>;
  lastEntries: StudentHistoryEntryDTO[];
}

// Entry type constants
export const STUDENT_HISTORY_ENTRY_TYPE = {
  STANDARD_LESSON: "standard_lesson",
  FUNDING_REQUEST: "funding_request",
  BALANCE_ADJUSTMENT: "balance_adjustment",
  REFUND: "refund",
  PARTICIPATION: "participation",
  EXTERNAL_PROVIDER_PARTICIPATION: "external_provider_participation",
} as const;

export type StudentHistoryEntryType = (typeof STUDENT_HISTORY_ENTRY_TYPE)[keyof typeof STUDENT_HISTORY_ENTRY_TYPE];

// Base fields common to all entries
interface BaseEntry {
  id: string;
  creationDate: string;
  totalAmount: number;
  note: Nullable<string>;
}

// Type-specific entry interfaces
export interface StandardLessonEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.STANDARD_LESSON;
  teacherName: string;
  teacherLastName: string;
  lessonDate: string;
  hours: number;
  studioPrice: number;
  studioLabel: string;
}

export interface FundingRequestEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.FUNDING_REQUEST;
  funderType: FUNDER | "afdas_hors_is";
  trainingTitle: string;
  trainingInterval: { start: string; end: string };
  externalReferenceId: string;
  obtentionDate: string;
}

export interface BalanceAdjustmentEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.BALANCE_ADJUSTMENT;
  label: string;
}

export interface RefundEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.REFUND;
  paymentLabel: Nullable<string>;
  iban: string;
}

export interface BatchParticipationEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.PARTICIPATION;
  batchTitle: string;
  organizer: {
    id: string;
    type: BATCH_ORGANIZER_TYPE;
    userName: string;
  };
  dateInterval: { start: string; end: string };
  invoiceStatus: "paid" | "unpaid";
}

export interface ExternalProviderParticipationEntry extends BaseEntry {
  type: typeof STUDENT_HISTORY_ENTRY_TYPE.EXTERNAL_PROVIDER_PARTICIPATION;
  trainingTitle: string;
  providerName: string;
  dateInterval: { start: string; end: string };
  paymentStatus: Nullable<"pending" | "paid" | "refused">;
}

export type StudentHistoryEntryDTO =
  | StandardLessonEntry
  | FundingRequestEntry
  | BalanceAdjustmentEntry
  | RefundEntry
  | BatchParticipationEntry
  | ExternalProviderParticipationEntry;

// ========================================
// Student Profile Types
// ========================================

export interface StudentVM {
  id: string;
  name: string;
  lastName: string;
  email: string;
  mobile: Nullable<string>;
  profilePictureUrl: Nullable<string>;
  stageName: Nullable<string>;
  canRequestARefund: boolean;
  canRequestANonAfdasISFunding: boolean;
}

export interface UpdateStudentBody {
  stageName: Nullable<string>;
}

export interface TeacherVM {
  id: string;
  name: string;
  lastName: string;
  email: string;
  /** Ready-to-use public profile URL, built by the backend; null when the teacher has no published public page. */
  publicProfileUrl: Nullable<string>;
}

// ========================================
// Refund Types
// ========================================

export interface RefundRequestBody {
  invoices: {
    amount: number;
    url: string;
  }[];
  iban: string;
  paymentLabel: string;
  note: Nullable<string>;
}

// ========================================
// Funding Request Types
// ========================================

interface FunderEligibility {
  isEligible: boolean;
  blockers: {
    hasActiveRequest: boolean;
    nextAfdasISRequestDateNotReached?: boolean;
  };
  details?: {
    afdasISWaitingPeriodEndDate?: string | null;
    nextPossibleAfdasISRequestDate?: string;
  };
}

export interface FundingRequestEligibilityVM {
  isEligible: boolean;
  globalBlockers: {
    balanceAboveCeiling: boolean;
  };
  globalDetails: {
    balanceCeiling: number;
  };
  funders: Record<FUNDER, FunderEligibility>;
}

export interface CreateFundingRequestBody {
  trainingItems: TrainingItem[];
  specializations: MAIN_SPECIALIZATIONS[];
  // Free-text "Autre" specialization; string(1..80) when the "Autre" checkbox is checked, null otherwise.
  otherSpecialization: Nullable<string>;
  resumeOrBioUrl: string;
  coverLetter: string;
  personalizedEducationalProject: string;
  funder: FUNDER_TYPE;
  specificAccommodation: Nullable<string>;
  afdasTravelExpenses: number | null;
  afdasAccommodationExpenses: number | null;
  afdasHasPersonalVehicle: boolean;
  afdasTrainingDates: { date: string }[] | null;
  studentNote: Nullable<string>;
}

export type GenerateCoverLetterBody = Partial<CreateFundingRequestBody>;

export interface GenerateCoverLetterResponse {
  generatedText: string;
}

export interface StudentFundingRequestVM {
  id: string;
  creationDate: string;
  obtentionDate: Nullable<string>;
  interval: Nullable<DateInterval>;
  funder: FUNDER_TYPE | { type: "afdas_hors_is" };
  trainingName: string;
  amount: Nullable<number>;
  externalFundingRequestId: Nullable<string>;
  status: FUNDING_REQUEST_STATUS;
  educationalProjectUrl: Nullable<string>;
  invitationUrl: Nullable<string>;
  studentAttendanceCertificateUrl: Nullable<string>;
  studentCompletionCertificate: Nullable<string>;
  quoteUrl: Nullable<string>;
}

// ========================================
// Student Repository Interface
// ========================================

export interface StudentRepository {
  // Profile (merged from StudentGateway)
  profile(): Promise<StudentVM>;
  update(body: UpdateStudentBody): Promise<void>;
  summary(): Promise<StudentSummaryVM>;
  teacherList(): Promise<TeacherVM[]>;
  favoriteTeacherList(): Promise<TeacherVM[]>;
  // Attendance Sheets
  getAllAttendanceSheets(): Promise<AttendanceSheetVM[]>;
  getUnsignedAttendanceSheets(): Promise<AttendanceSheetVM[]>;
  signAttendanceSheet(body: SignAttendanceSheetBody): Promise<SignAttendanceSheetResponse>;
  signAllAttendanceSheets(body: SignAllAttendanceSheetsBody): Promise<SignAllAttendanceSheetsResponse>;

  // Pending Actions
  hasPendingActions(): Promise<StudentPendingActionsVM>;
  getPendingSurveys(): Promise<PendingSurveyVM[]>;
  getPendingObjectiveAssignments(): Promise<PendingObjectiveAssignmentGroupVM[]>;

  // Training Items
  getAllTrainingItems(): Promise<TrainingItemVM[]>;
  getTrainingItemById(id: string, type: TRAINING_ITEM_TYPE): Promise<TrainingItemDetailsVM>;

  // Manage Objectives
  manageObjectives(command: ManageObjectivesCommand): Promise<void>;

  // Submit Survey
  submitSurvey(command: SubmitSurveyCommand): Promise<void>;

  // External Provider Batch Request
  createExternalProviderBatchRequest(body: CreateExternalProviderBatchRequestBody): Promise<string>;

  // Refund Requests
  requestRefund(refund: RefundRequestBody): Promise<void>;
  uploadInvoice(file: File): Promise<string>;

  // Funding Requests
  createFundingRequest(fundingRequest: CreateFundingRequestBody): Promise<string>;
  fundingRequestEligibility(): Promise<FundingRequestEligibilityVM>;
  getAllFundingRequests(): Promise<StudentFundingRequestVM[]>;
  getEligibleTrainingItems(): Promise<EligibleTrainingItemsVM>;

  // Cover Letter Generation
  generateCoverLetter(body: GenerateCoverLetterBody): Promise<GenerateCoverLetterResponse>;
}
